import { useI18n } from '../../../../../i18n';
import type { Flujograma } from '../types/flowchart.types';
import { FlujogramaWorkflowBar } from '../components/FlujogramaWorkflowBar';
import { ArtefactoToolbarActions } from '../../../../shared/components/ArtefactoToolbarActions';

// 0661.1 — header unificado cross-artefacto. Eliminados:
//   - <VersionSelector> dropdown legacy (lo provee el wrapper si tieneVersionado=true)
//   - formatDate fecha actualizacion (info redundante)
//   - botones legacy "Nueva edición" / "Eliminar" / "Archivar" (los provee el wrapper)
//   - banner SizeWarning (regla 30 nodos eliminada · ver FlowEditor F5)
// Resultado: misma UI/UX que OrganigramaPage (1 línea con wrapper + toolbar shared).

interface FlowHeaderProps {
  procesoId: string;
  procesoCodigo?: string;
  procesoNombre?: string;
  flujograma: Flujograma | null;
  editing: boolean;
  saving: boolean;
  personName: string | null;
  onEnterEdit: () => void;
  onSave: () => void;
  onExitEdit: () => void;
  onDiscard: () => void;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  onViewModeChange?: (mode: 'ficha' | 'docs' | 'flow') => void;
}

const SEP = {
  width: 1,
  height: 20,
  background: '#e8e8e8',
  margin: '0 8px',
  flexShrink: 0,
} as const;

const BTN = {
  padding: '4px 12px',
  borderRadius: 5,
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
} as const;

export function FlowHeader({
  procesoId,
  procesoCodigo,
  procesoNombre,
  flujograma,
  editing,
  saving,
  personName,
  onEnterEdit,
  onSave,
  onExitEdit,
  onDiscard,
  isFullscreen,
  onToggleFullscreen,
  onViewModeChange,
}: FlowHeaderProps) {
  const { t } = useI18n();
  const displayCode = procesoCodigo ?? procesoId;
  const displayName = procesoNombre ?? t.process.flujograma;
  const isBorrador = flujograma?.estado === 'BORRADOR';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        height: 42,
        minHeight: 42,
        flexShrink: 0,
        borderBottom: '1px solid #e8e8e8',
        backgroundColor: '#fff',
        padding: '0 12px',
        fontSize: 13,
      }}
    >
      {/* Tabs [Ficha][Docs][Flow] — solo cuando hay callback de navegacion */}
      {onViewModeChange && (
        <>
          <div style={{ display: 'flex', gap: 2, marginRight: 4 }}>
            {(['ficha', 'docs', 'flow'] as const).map((m) => (
              <button
                key={m}
                onClick={() => onViewModeChange(m)}
                style={{
                  padding: '3px 10px',
                  fontSize: 10,
                  fontWeight: 600,
                  borderRadius: 4,
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  background: m === 'flow' ? '#1F3864' : 'transparent',
                  color: m === 'flow' ? '#fff' : 'rgba(0,0,0,0.50)',
                }}
              >
                {m === 'ficha' ? t.process.ficha : m === 'docs' ? t.process.docs : t.process.flow}
              </button>
            ))}
          </div>
          <div style={SEP} />
        </>
      )}

      {/* Codigo proceso (legible, no UUID) */}
      <span
        style={{
          fontFamily: 'ui-monospace, SFMono-Regular, monospace',
          fontWeight: 600,
          fontSize: 12,
          color: '#1A1D23',
          marginRight: 8,
        }}
      >
        {displayCode}
      </span>

      {/* Nombre */}
      <span
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: '#1A1D23',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap' as const,
          minWidth: 0,
          maxWidth: 200,
          marginRight: 8,
        }}
      >
        {displayName}
      </span>

      {personName && (
        <span style={{ fontSize: 11, color: '#6B7280', marginLeft: 4 }}>{personName}</span>
      )}

      <div style={{ flex: 1 }} />

      {/* Workflow bar centralizada (ADR-025 ext 0661 + 0661.1) — provee:
          - botones acción legales (ENVIAR_REVISION/APROBAR/RECHAZAR/PUBLICAR/ARCHIVAR/DESCARTAR)
          - confirm modal centralizado + motivo rechazo
          Sustituye los botones legacy "Eliminar borrador" / "Nueva edición" / "Archivar".
          0661.1: prop renombrada `flowchartId` → `procesoId` (el wrapper hace
          GET /flujogramas/:procesoId/latest internamente). */}
      {!editing && (
        <>
          <FlujogramaWorkflowBar procesoId={procesoId} enabled />
          <div style={SEP} />
        </>
      )}

      {/* Acciones del editor (durante edit) o acceso al editor (BORRADOR) */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {editing ? (
          <>
            {saving && (
              <span style={{ fontSize: 11, color: '#9CA3AF', marginRight: 4 }}>
                {t.flow.guardando}
              </span>
            )}
            <button
              onClick={onSave}
              disabled={saving}
              style={{
                ...BTN,
                border: 'none',
                background: '#059669',
                color: 'white',
                opacity: saving ? 0.7 : 1,
              }}
            >
              {t.flow.guardar}
            </button>
            <button
              onClick={onExitEdit}
              style={{
                ...BTN,
                fontWeight: 500,
                border: '1px solid #d5d5d5',
                background: '#fff',
                color: '#374151',
              }}
            >
              {t.flow.guardarYCerrar}
            </button>
            <button
              onClick={onDiscard}
              style={{
                ...BTN,
                fontWeight: 500,
                border: '1px solid #FECACA',
                background: '#FEF2F2',
                color: '#DC2626',
              }}
            >
              {t.flow.descartar}
            </button>
          </>
        ) : (
          isBorrador && (
            <button
              onClick={onEnterEdit}
              style={{
                ...BTN,
                border: '1px solid #d5d5d5',
                background: '#fff',
                color: '#374151',
              }}
            >
              {t.flow.editar}
            </button>
          )
        )}

        {/* Toolbar canónico shared cross-artefacto · solo Fullscreen para flujograma. */}
        <ArtefactoToolbarActions
          onFullscreen={onToggleFullscreen}
          isFullscreen={isFullscreen}
          compact
        />
      </div>
    </div>
  );
}
